import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";
import { useChartTokens } from "../../theme/chartTokens";
import ChartTooltip from "./ChartTooltip";

const MAX_LABEL = 16;

/** Category tick that always stays on one line, truncating with an ellipsis. */
function SingleLineTick({ x, y, payload, fill }) {
  const raw = String(payload?.value ?? "");
  const label = raw.length > MAX_LABEL ? `${raw.slice(0, MAX_LABEL - 1)}…` : raw;

  return (
    <text x={x} y={y} dy={4} textAnchor="end" fill={fill} fontSize={12}>
      {label}
    </text>
  );
}

/**
 * Hours by project — magnitude, so one hue rather than a colour per project
 * (colour here would imply identity the reader doesn't need). Horizontal, since
 * project names are long, and sorted so rank is readable without labels.
 *
 * `data`: [{ name: string, hours: number }] already sorted descending.
 */
export default function HoursByProjectChart({
  data,
  height = 260,
  // Defaults keep the hours callers unchanged; the expense report reuses the
  // same chart for money by overriding these.
  dataKey = "hours",
  seriesName = "Hours",
  formatValue = (value) => `${Number(value).toFixed(2)} h`,
  formatLabel = (value) => Number(value).toFixed(1),
}) {
  const t = useChartTokens();

  return (
    <ResponsiveContainer width="100%" height={Math.max(height, data.length * 38 + 24)}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 0, right: 44, bottom: 0, left: 0 }}
        barCategoryGap={8}
      >
        <CartesianGrid stroke={t.grid} horizontal={false} />

        <XAxis
          type="number"
          tickLine={false}
          axisLine={false}
          tick={{ fill: t.axisInk, fontSize: 11 }}
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          tickLine={false}
          axisLine={false}
          width={118}
          // Custom tick because recharts wraps long names onto a second line,
          // which then collides with the neighbouring bar. Full name is in
          // the tooltip.
          tick={<SingleLineTick fill={t.axisInk} />}
        />

        <Tooltip
          cursor={{ fill: t.grid }}
          content={<ChartTooltip formatValue={formatValue} />}
        />

        <Bar
          dataKey={dataKey}
          name={seriesName}
          fill={t.series}
          radius={[0, 4, 4, 0]}
          maxBarSize={22}
          isAnimationActive={false}
        >
          {/* Direct labels: the value is the point, so don't make readers use the axis. */}
          <LabelList
            dataKey={dataKey}
            position="right"
            offset={8}
            fill={t.axisInk}
            fontSize={11}
            formatter={formatLabel}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
